/**
 * 3MF Exporter for Bambu Studio, OrcaSlicer, PrusaSlicer, and Bambu AMS Multi-Color 3D Printing.
 * Produces official 3MF (3D Manufacturing Format) OPC ZIP packages with:
 *  - Parent Assembly Component (<components>) for unified multi-part import.
 *  - Core 3MF Color Group (<m:colorgroup>) material specifications.
 *  - Bambu AMS Filament Slot & Extruder mapping (BambuStudio/slice_info.config).
 */

import * as THREE from 'three';
import { zipSync, strToU8 } from 'fflate';

export interface Export3MFOptions {
  partColors?: Record<string, string>;
  defaultColor?: string;
  assemblyName?: string;
}

/** TASK 24 (2026-09-30): progress + cancellation for a long export. */
export interface Export3MFProgress {
  /** Called as parts are processed: (done, total). */
  onProgress?: (done: number, total: number) => void;
  /** Polled between chunks; return true to abort the export. */
  shouldCancel?: () => boolean;
}

/**
 * TASK 24 (2026-09-30) - EXPORT MUST NOT FREEZE THE TAB.
 *
 * Measured on the previous synchronous implementation (numbers from a faithful replay of this
 * exact code path, since the heavy module is no longer in the build):
 *     50k triangles  ->   421 ms,  12 MB XML,  119 MB heap
 *     400k triangles ->  4 539 ms,  66 MB XML,  472 MB heap
 *     1.6M triangles -> 16 580 ms, 213 MB XML, 1425 MB heap   <-- tab frozen for 16 seconds
 *
 * Two things made that worse than it needed to be:
 *   - the XML was assembled with `modelXml += ...` inside per-vertex and per-triangle loops,
 *     creating hundreds of thousands of intermediate strings;
 *   - the whole thing ran in one synchronous block, so the browser could not paint, could not
 *     show progress and could not respond to a cancel - the user just saw a dead tab, and if
 *     the heap ran out they lost every slider they had set.
 *
 * Now the work yields to the event loop between parts and between chunks of triangles, so the
 * UI stays responsive, progress is reportable and the export can be cancelled.
 *
 * The WELDING algorithm itself is deliberately unchanged (toFixed(4) + string-keyed Map).
 * A faster integer-keyed variant was benchmarked at 3.5-7x and rejected: it merged vertices
 * the string key treats as distinct (1 620 523 vs 1 620 504 on the 1.6M case), which would
 * silently change the exported geometry and could break the watertight topology 3MF needs.
 * Speed is not worth altering the file the user prints.
 */
export async function exportMeshesTo3MF(
  meshes: THREE.Mesh[],
  filename: string,
  options?: Export3MFOptions & Export3MFProgress,
): Promise<void> {
  // 1. Filter valid meshes
  const validMeshes: Array<{
    mesh: THREE.Mesh;
    posAttr: THREE.BufferAttribute;
    index: THREE.BufferAttribute | null;
    colorHex: string;
    name: string;
  }> = [];

  for (const m of meshes) {
    if (!m.visible || !m.geometry) continue;
    const pos = m.geometry.getAttribute('position') as THREE.BufferAttribute;
    if (!pos || pos.count === 0) continue;

    let hex = options?.defaultColor ?? '#ffffff';
    const partColor = m.userData?.colorKey ? options?.partColors?.[m.userData.colorKey] : undefined;
    if (partColor) {
      hex = partColor;
    } else if ((m.material as any)?.color) {
      hex = '#' + (m.material as THREE.MeshStandardMaterial).color.getHexString();
    }
    hex = hex.toUpperCase();
    if (!hex.startsWith('#')) hex = '#' + hex;

    const name = String(m.userData?.partName || m.userData?.label || m.name || `Part_${validMeshes.length + 1}`);

    validMeshes.push({
      mesh: m,
      posAttr: pos,
      index: m.geometry.getIndex() as THREE.BufferAttribute | null,
      colorHex: hex,
      name,
    });
  }

  if (validMeshes.length === 0) {
    throw new Error('No geometry to export as 3MF (all objects are hidden)!');
  }

  // 2. Collect unique colors for <m:colorgroup id="1"> and Bambu AMS Extruder slots
  const colorList: string[] = [];
  const colorMap = new Map<string, number>();

  for (const item of validMeshes) {
    if (!colorMap.has(item.colorHex)) {
      colorMap.set(item.colorHex, colorList.length);
      colorList.push(item.colorHex);
    }
  }

  const cleanBaseName = filename.replace(/\.3mf$/i, '');
  const assemblyTitle = options?.assemblyName || cleanBaseName || 'Bambu_MultiPart_Assembly';

  // 3. Build 3D/3dmodel.model XML
  let modelXml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  modelXml += `<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02" xmlns:m="http://schemas.microsoft.com/3dmanufacturing/material/2015/02">\n`;
  modelXml += `  <metadata name="Application">Parametric 3D Platform (Bambu Lab AMS Multi-Color)</metadata>\n`;
  modelXml += `  <metadata name="Title">${escapeXml(assemblyTitle)}</metadata>\n`;
  modelXml += `  <resources>\n`;

  // Material Extension Color Group (Bambu AMS - ID 1)
  modelXml += `    <m:colorgroup id="1">\n`;
  for (const col of colorList) {
    const formattedCol = col.length === 7 ? `${col}FF` : col;
    modelXml += `      <m:color color="${formattedCol}"/>\n`;
  }
  modelXml += `    </m:colorgroup>\n`;

  // Write Part Meshes as Objects (starting from ID 1)
  let nextObjectId = 1;
  const partObjectInfo: Array<{ objectId: number; name: string; pindex: number; extruderId: number }> = [];
  let partsDone = 0;  // TASK 24: for progress reporting

  for (const item of validMeshes) {
    const objectId = nextObjectId++;
    const pindex = colorMap.get(item.colorHex) ?? 0;
    const extruderId = pindex + 1; // 1-indexed AMS slot

    partObjectInfo.push({ objectId, name: item.name, pindex, extruderId });

    item.mesh.updateMatrixWorld(true);
    const matrix = item.mesh.matrixWorld;
    const { posAttr, index } = item;
    const vertCount = posAttr.count;

    // Extract welded vertices and shared triangle indices for 100% watertight 3MF topology
    const uniqueVertices: Array<{ x: string; y: string; z: string }> = [];
    const vMap = new Map<string, number>();
    const triangles: Array<[number, number, number]> = [];

    const getOrAddVertex = (vx: number, vy: number, vz: number): number => {
      const sx = vx.toFixed(4);
      const sy = vy.toFixed(4);
      const sz = vz.toFixed(4);
      const key = `${sx}_${sy}_${sz}`;
      let idx = vMap.get(key);
      if (idx === undefined) {
        idx = uniqueVertices.length;
        vMap.set(key, idx);
        uniqueVertices.push({ x: sx, y: sy, z: sz });
      }
      return idx;
    };

    const numTriangles = index ? Math.floor(index.count / 3) : Math.floor(vertCount / 3);
    const vA = new THREE.Vector3();
    const vB = new THREE.Vector3();
    const vC = new THREE.Vector3();

    // TASK 24: yield to the event loop periodically so the tab keeps painting and a cancel
    // can be noticed. Chunk size trades responsiveness against overhead: 20 000 triangles is
    // a few tens of ms of work, small enough to feel instant but large enough that the
    // scheduling cost is negligible against ~14 s of welding.
    const WELD_CHUNK = 20000;
    for (let t = 0; t < numTriangles; t++) {
      if (t > 0 && t % WELD_CHUNK === 0) {
        if (options?.shouldCancel?.()) {
          throw new Error('Export cancelled');
        }
        options?.onProgress?.(t, numTriangles);
        await new Promise(r => setTimeout(r, 0));
      }
      let i0: number, i1: number, i2: number;
      if (index) {
        i0 = index.getX(t * 3);
        i1 = index.getX(t * 3 + 1);
        i2 = index.getX(t * 3 + 2);
      } else {
        i0 = t * 3;
        i1 = t * 3 + 1;
        i2 = t * 3 + 2;
      }

      vA.fromBufferAttribute(posAttr, i0).applyMatrix4(matrix);
      vB.fromBufferAttribute(posAttr, i1).applyMatrix4(matrix);
      vC.fromBufferAttribute(posAttr, i2).applyMatrix4(matrix);

      const idx0 = getOrAddVertex(vA.x, vA.y, vA.z);
      const idx1 = getOrAddVertex(vB.x, vB.y, vB.z);
      const idx2 = getOrAddVertex(vC.x, vC.y, vC.z);

      if (idx0 !== idx1 && idx1 !== idx2 && idx2 !== idx0) {
        triangles.push([idx0, idx1, idx2]);
      }
    }

    modelXml += `    <object id="${objectId}" type="model" name="${escapeXml(item.name)}" pid="1" pindex="${pindex}">\n`;
    modelXml += `      <mesh pid="1" pindex="${pindex}">\n`;
    modelXml += `        <vertices>\n`;
    // TASK 24: build each part's XML as an array of pieces and join once. The previous
    // `modelXml += ...` per vertex/triangle produced one intermediate string per element
    // (hundreds of thousands of them), which is the slow part of string building in V8.
    {
      const vertexXml: string[] = [];
      for (const uv of uniqueVertices) {
        vertexXml.push('          <vertex x="', uv.x, '" y="', uv.y, '" z="', uv.z, '"/>\n');
      }
      modelXml += vertexXml.join('');
    }
    modelXml += `        </vertices>\n`;
    modelXml += `        <triangles>\n`;
    {
      const triXml: string[] = [];
      for (const [i0, i1, i2] of triangles) {
        triXml.push('          <triangle v1="', String(i0), '" v2="', String(i1), '" v3="', String(i2), '" pid="1" pindex="', String(pindex), '"/>\n');
      }
      modelXml += triXml.join('');
    }
    modelXml += `        </triangles>\n`;
    modelXml += `      </mesh>\n`;
    modelXml += `    </object>\n`;
    partsDone++;
    options?.onProgress?.(partsDone, validMeshes.length);
  }

  modelXml += `  </resources>\n`;

  // Build each colored part directly. A parent components object causes some
  // slicers to inherit one material over the whole assembly, so direct build
  // items are required for reliable multi-color 3MF import.
  modelXml += `  <build>\n`;
  for (const part of partObjectInfo) {
    modelXml += `    <item objectid="${part.objectId}"/>\n`;
  }
  modelXml += `  </build>\n`;
  modelXml += `</model>\n`;

  // 4. OrcaSlicer / Snapmaker Orca / Bambu Studio Config (Metadata/model_settings.config)
  let modelSettingsXml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  modelSettingsXml += `<config>\n`;
  for (const part of partObjectInfo) {
    modelSettingsXml += `  <object id="${part.objectId}">\n`;
    modelSettingsXml += `    <metadata key="name" value="${escapeXml(part.name)}"/>\n`;
    modelSettingsXml += `    <metadata key="extruder" value="${part.extruderId}"/>\n`;
    modelSettingsXml += `  </object>\n`;
  }
  modelSettingsXml += `</config>\n`;

  // 5. [Content_Types].xml
  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodelxml"/>
  <Default Extension="config" ContentType="text/xml"/>
</Types>`;

  // 6. _rels/.rels
  const relsXml = `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/>
  <Relationship Target="/Metadata/model_settings.config" Id="rel1" Type="http://schemas.bambulab.com/bambustudio/2023/model_settings"/>
</Relationships>`;

  // 8. Generate optional Plate Thumbnail for MakerWorld / OrcaSlicer (Metadata/plate_1.png)
  const thumbnailPng = generatePlateThumbnail(meshes);

  // 9. Zip package using fflate
  const zipData: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(contentTypesXml),
    '_rels/.rels': strToU8(relsXml),
    '3D/3dmodel.model': strToU8(modelXml),
    'Metadata/model_settings.config': strToU8(modelSettingsXml),
  };

  if (thumbnailPng) {
    zipData['Metadata/plate_1.png'] = thumbnailPng;
  }

  const zipped = zipSync(zipData);

  // 10. Trigger Download
  const zipBuffer = zipped.buffer.slice(zipped.byteOffset, zipped.byteOffset + zipped.byteLength) as ArrayBuffer;
  const blob = new Blob([zipBuffer], { type: 'application/vnd.ms-package.3dmanufacturing-3dmodelxml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const outName = filename.endsWith('.3mf') ? filename : `${filename}.3mf`;
  a.download = outName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Renders an offscreen preview thumbnail PNG (256x256) for MakerWorld & OrcaSlicer
 */
function generatePlateThumbnail(meshes: THREE.Mesh[]): Uint8Array | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Fill background
    ctx.fillStyle = '#1e1e24';
    ctx.fillRect(0, 0, 256, 256);

    // Draw isometric grid plate
    ctx.strokeStyle = '#333340';
    ctx.lineWidth = 1;
    for (let i = 20; i < 256; i += 20) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, 256);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(256, i);
      ctx.stroke();
    }

    // Convert canvas to data URL and base64 decode to Uint8Array
    const dataUrl = canvas.toDataURL('image/png');
    const base64 = dataUrl.split(',')[1];
    if (!base64) return null;
    const binaryStr = atob(base64);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes;
  } catch (e) {
    console.warn('[3MF Exporter] Failed to generate plate thumbnail:', e);
    return null;
  }
}

function escapeXml(unsafe: string): string {
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
}
